import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import FabricSubCategory from '@components/commonFilters/materials/Fabrics/FabricSubCategory';
import CustomDropdown from '@components/CustomDropdown';
import ModalContainer from '@components/modals/ModalContainer';
import ModalHeader from '@components/modals/ModalHeader';

import { WEIGHT_TYPES } from '@features/materials/MaterialConstants';
import UploadKeyProduct from '@features/supplier/component/UploadKeyProduct';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { addFabricWithSupplier, getCompositionParts } from '@services/supplierOnboardingService';

import {
	compositionCalculation,
	compositionDefaultFieldSet,
	fetchCompositionNameList,
	onChangeCompositionPercentage,
	onChangeCompositionSelection
} from '@utils/MaterialsUtil';

const KeyProductFabric = ({ onClose, onSuccess }) => {
	const [documentList, setDocumentList] = useState([]);
	const { id } = useParams();
	const dispatch = useDispatch();
	const isLoading = useSelector((store) => store.loaderStore);
	const [inputData, setInputData] = useState({});
	const [fabricConstruction, setFabricConstruction] = useState({});
	const [selectedWeightType, setSelectedWeightType] = useState({});
	const [errors, setErrors] = useState({});
	const [compositionNameList, setCompositionNameList] = useState([]);
	const [compositionFieldInfoList, setCompositionFieldInfoList] = useState(
		compositionDefaultFieldSet(2, [])
	);
	const [fabricTypeList, setFabricTypeList] = useState([]);

	useEffect(() => {
		(async () => {
			await fetchCompositionNameList(setCompositionNameList);
		})();
	}, []);

	const isValid = () => {
		let err = {};
		if (!fabricConstruction?.fabricType) {
			err['fabricTypeError'] = 'Fabric type is required!';
		}
		if (!inputData?.gsm || !selectedWeightType?.value) {
			err['weightError'] = 'Weight is required!';
		}
		setErrors(err);
		return Object.keys(err).length <= 0;
	};

	const onSave = async () => {
		if (isValid()) {
			try {
				let compositionCalObj = compositionCalculation(compositionFieldInfoList);
				if (compositionCalObj.percentageIsNotOk) {
					setErrors({ ...errors, compositionError: 'Total composition needs to be 100%' });
					return false;
				} else if (compositionCalObj.fiberNameIsNotOk) {
					setErrors({ ...errors, compositionError: 'Select composition' });
					return false;
				} else {
					const data = await getCompositionParts(compositionCalObj.compositionPart);
					if (data.length === 0) {
						setErrors({ ...errors, compositionError: 'Composition is wrong' });
					} else {
						dispatch(openLoader());
						let selectedImageIds = documentList.map((item) => item.id);
						let postData = {
							fabricRequest: {
								gsm: inputData.gsm,
								fabricCompositionPartIdList: data,
								weightType: selectedWeightType.id,
								fabricType: fabricConstruction?.fabricType,
								documentIdList: selectedImageIds,
								addType: 'Add',
								materialType: 'MAIN_FABRIC'
							},
							supplierMaterialRequestList: [
								{
									price: inputData.price,
									referenceNumber: inputData.fabricReferenceNo,
									supplierId: id,
									requestedPage: 'SUPPLIER'
								}
							]
						};
						await addFabricWithSupplier(postData);
						await onSuccess();
						dispatch(closeLoader());
					}
				}
			} catch {
				dispatch(closeLoader());
			}
		}
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setInputData((prev) => ({
			...prev,
			[name]: value
		}));
	};

	const compositionRender = (obj, index) => {
		return (
			<div className="col-md-6" key={`composition_${index}`}>
				<div className="form-group composition-item mb-4">
					<div className="d-flex">
						<input
							type="number"
							defaultValue={obj?.percentage}
							min={0.1}
							max={100}
							onBlur={(e) => {
								onChangeCompositionPercentage(
									e.target.value,
									index,
									compositionFieldInfoList,
									setCompositionFieldInfoList
								);
							}}
							className="input-percentage"
						/>
						<p className="percentage">%</p>
						<CustomDropdown
							isDisabled={!obj?.percentage}
							type="fabricName"
							title={'Composition'}
							toolTipLength={13}
							onItemClick={(value) =>
								onChangeCompositionSelection(
									value,
									index,
									compositionFieldInfoList,
									setCompositionFieldInfoList
								)
							}
							items={compositionNameList}
							selectedItem={obj?.fiberName}
							searchPlaceholder="Composition"
						/>
					</div>
				</div>
			</div>
		);
	};

	return (
		<ModalContainer className="products-upload-modal">
			<div className="common-popup">
				<ModalHeader title="Your Key Product (Fabric)" onClose={onClose} />
				<div className="common-popup-body">
					<div className="row">
						<UploadKeyProduct
							fieldName="materialsUpload"
							setDocumentList={setDocumentList}
							documentList={documentList}
						/>

						<div className="col-md-6 pr-2">
							<div className="form-group">
								<div className="select-component">
									<label htmlFor="collection">Fabric</label>
									<FabricSubCategory
										onSelectCallBack={(value) => setFabricConstruction(value)}
										type={'INPUT'}
										fabricTypeList={fabricTypeList}
										setFabricTypeList={setFabricTypeList}
									/>
								</div>
								{errors?.fabricTypeError && <p className="error">{errors.fabricTypeError}</p>}
							</div>
						</div>
						<div className="col-md-6">
							<div className="form-group composition-item mb-0">
								<label>Weight*</label>
								<div className="d-flex">
									<input
										type="number"
										min="0.1"
										max={100}
										className="input-percentage"
										value={inputData?.gsm || ''}
										name="gsm"
										onChange={handleChange}
									/>
									<p className="percentage weight-percentage">%</p>
									<CustomDropdown
										type="fabricName"
										title={'Select'}
										items={WEIGHT_TYPES}
										toolTipLength={13}
										isShowSearch={false}
										onItemClick={(item) => setSelectedWeightType(item)}
										selectedItem={selectedWeightType?.value}
									/>
								</div>
								{errors?.weightError && <p className="error">{errors.weightError}</p>}
							</div>
						</div>
						<div className="composition-row w-100">
							<p className="regular-14 gray_dark_02 pb-1">Composition*</p>
							<div className="row  p-0 m-0 ">
								{compositionFieldInfoList?.map((obj, index) => compositionRender(obj, index))}
								{errors?.compositionError && (
									<p className="error">{errors.compositionError}</p>
								)}
							</div>
						</div>
						<div className="col-md-6 pr-2">
							<div className="form-group">
								<label htmlFor="collection">Price</label>
								<input
									type="text"
									value={inputData?.price || ''}
									name="price"
									onChange={handleChange}
								/>
							</div>
						</div>
						<div className="col-md-6 pr-2">
							<div className="form-group">
								<label htmlFor="collection">Fabric Reference Number</label>
								<input
									type="text"
									value={inputData?.fabricReferenceNo || ''}
									name="fabricReferenceNo"
									onChange={handleChange}
								/>
							</div>
						</div>
					</div>
				</div>
				<div className="modal-footer">
					<div className="footer-buttons text-right">
						<button className="button gray-outline size32" onClick={onClose}>
							Cancel
						</button>
						<button disabled={isLoading} className="button size32 ml-2" onClick={() => onSave()}>
							Submit
						</button>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default KeyProductFabric;
